import { useTranslation } from 'react-i18next';
import { formatDateOnly } from '~/utils';
import { contactLabel } from '~/utils/contact';
import type { TableColumn } from '~/components/ui/table';
import type { TableCell } from '~/components/ui/table/Table';
import type {
  BuyListQuery,
  MyBuyListsQuery,
  TeamBuyListsQuery,
} from '~/graphql/graphql';

export type BuyListRecord =
  | MyBuyListsQuery['myBuyLists']['content'][number]
  | TeamBuyListsQuery['teamBuyLists']['content'][number]
  | BuyListQuery['buyList'];

type BuyListColumnsOptions = {
  showBuyerColumn: boolean;
  isSortable: boolean;
  noteMaxWidth?: number;
};

export function useBuyListColumns({
  showBuyerColumn,
  isSortable,
  noteMaxWidth,
}: BuyListColumnsOptions): TableColumn[] {
  const { t } = useTranslation('inventory');

  const buyerColumn: TableColumn[] = showBuyerColumn
    ? [{ key: 'buyer', header: t('inventory.buy_list.buyer'), width: 12 }]
    : [];

  return [
    {
      key: 'products',
      header: t('inventory.buy_list.products'),
      width: 8,
      isSortable,
    },
    { key: 'location', header: t('inventory.buy_list.location'), width: 16 },
    { key: 'vehicle', header: t('inventory.buy_list.vehicle'), width: 10 },
    {
      key: 'expectedShipDate',
      header: t('inventory.buy_list.ship_week'),
      width: 11,
      isSortable,
    },
    {
      key: 'note',
      header: t('inventory.buy_list.note'),
      width: 14,
      maxWidth: noteMaxWidth,
    },
    ...buyerColumn,
  ];
}

export function buyListNoteTestId(testId: string, buyListId: string): string {
  return `${testId}-note-${buyListId}`;
}

export function vehicleCell(buyList: BuyListRecord, testId: string): TableCell {
  return {
    value: (
      <span data-testid={`${testId}-vehicle-${buyList.id}`}>
        {buyList.transportVehicle.name}
      </span>
    ),
  };
}

export function expectedShipDateCell(buyList: BuyListRecord): TableCell {
  return { value: formatDateOnly(buyList.expectedShipDate) };
}

export function buyerCell(buyList: BuyListRecord): TableCell {
  return {
    value:
      'initiatingUser' in buyList ? contactLabel(buyList.initiatingUser) : '',
  };
}
